import type { CaptureResult } from 'posthog-js';
import { isPreloadErrorMessage } from '@/common/utils/preloadError.util';

type ExceptionItem = {
  type?: string;
  value?: string;
};

type ExceptionProperties = {
  $exception_list?: ExceptionItem[];
  error_code?: string;
};

type DenyPredicate = (event: CaptureResult, exception: ExceptionItem | null) => boolean;

export const EXCEPTION_DEDUPE_WINDOW_MS = 5000;

const CHUNK_PRELOAD_FAILURE_ERROR_CODE = 'chunk_preload_failure';

const getException = (event: CaptureResult): ExceptionItem | null => {
  const list = (event.properties as ExceptionProperties)?.$exception_list;
  return Array.isArray(list) && list.length > 0 ? list[0] : null;
};

const getErrorCode = (event: CaptureResult): string | undefined => (event.properties as ExceptionProperties)?.error_code;

const DENY_PREDICATES: DenyPredicate[] = [
  (_, ex) => ex?.type === 'CancellationError',
  (_, ex) => ex?.value === 'Cancel action',
  (_, ex) => ex?.type === 'AbortError',
  (_, ex) => ex?.value === 'The operation was aborted.',
  (_, ex) => /could\s*not\s*be\s*found/i.test(ex?.value ?? ''),
  (e) => ['s3_not_found', 's3_disabled', 's3_user_disabled'].includes(getErrorCode(e) ?? ''),
];

const isChunkPreloadFailure = (exception: ExceptionItem | null): boolean => {
  const exceptionMessage = exception?.value;
  return typeof exceptionMessage === 'string' && isPreloadErrorMessage(exceptionMessage);
};

const tagChunkPreloadFailure = (event: CaptureResult): void => {
  const properties = event.properties as ExceptionProperties;
  if (!properties.error_code) {
    properties.error_code = CHUNK_PRELOAD_FAILURE_ERROR_CODE;
  }
};

const buildExceptionSignature = (exception: ExceptionItem): string => `${exception.type ?? ''}::${exception.value ?? ''}`;

const lastEmittedAtBySignature = new Map<string, number>();

export const getDedupeCacheSize = (): number => lastEmittedAtBySignature.size;

const isDuplicateWithinDedupeWindow = (signature: string): boolean => {
  const capturedAt = Date.now();
  const lastEmittedAt = lastEmittedAtBySignature.get(signature);
  const isDuplicate = typeof lastEmittedAt === 'number'
    && capturedAt - lastEmittedAt < EXCEPTION_DEDUPE_WINDOW_MS;

  if (!isDuplicate) {
    lastEmittedAtBySignature.forEach((emittedAt, knownSignature) => {
      if (capturedAt - emittedAt >= EXCEPTION_DEDUPE_WINDOW_MS) {
        lastEmittedAtBySignature.delete(knownSignature);
      }
    });
    lastEmittedAtBySignature.set(signature, capturedAt);
  }

  return isDuplicate;
};

const shouldFilterError = (event: CaptureResult | null): boolean => {
  if (!event || event.event !== '$exception') {
    return false;
  }

  const exception = getException(event);

  if (DENY_PREDICATES.some(predicate => predicate(event, exception))) {
    return true;
  }

  if (!exception) {
    return false;
  }

  if (isChunkPreloadFailure(exception)) {
    tagChunkPreloadFailure(event);
  }

  return false;
};

/**
 * Stateful companion to `shouldFilterError`, deliberately kept separate: `before_send` runs the
 * filter on every captured event AND again on every buffered event it replays, so a window check
 * folded into the filter would see one incident twice and drop the replay. This must therefore be
 * called exactly once per event, on the path where the event is actually sent.
 */
export const isDuplicateChunkPreloadFailure = (event: CaptureResult | null): boolean => {
  if (!event || event.event !== '$exception') {
    return false;
  }

  const exception = getException(event);

  if (!exception || !isChunkPreloadFailure(exception)) {
    return false;
  }

  return isDuplicateWithinDedupeWindow(buildExceptionSignature(exception));
};

export default shouldFilterError;
